import { useState } from 'react';
import { IMedal } from '../../../../interfaces/IMedals';

const SLIDER_CSS = `
  .gmc-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 999px;
    outline: none;
    cursor: pointer;
    transition: background 0.15s;
  }
  .gmc-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #00BFFF;
    border: 3px solid #fff;
    box-shadow: 0 0 10px rgba(0,191,255,0.5);
    cursor: pointer;
  }
  .gmc-slider::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #00BFFF;
    border: 3px solid #fff;
    box-shadow: 0 0 10px rgba(0,191,255,0.5);
    cursor: pointer;
  }
`;

function injectStyles() {
  if (
    typeof document !== 'undefined' &&
    !document.getElementById('gmc-styles')
  ) {
    const tag = document.createElement('style');
    tag.id = 'gmc-styles';
    tag.textContent = SLIDER_CSS;
    document.head.appendChild(tag);
  }
}

const MIN = 50000;
const MAX = 2000000;
const STEP = 10000;

/* Color palette applied by tier index (cycles if more than 4 tiers) */
const TIER_PALETTE = [
  {
    labelColor: '#d97706',
    border: 'rgba(180,120,30,0.40)',
    bg: 'rgba(120,60,0,0.20)',
  },
  {
    labelColor: '#cbd5e1',
    border: 'rgba(150,160,180,0.40)',
    bg: 'rgba(80,90,110,0.20)',
  },
  {
    labelColor: '#facc15',
    border: 'rgba(200,160,0,0.50)',
    bg: 'rgba(100,70,0,0.20)',
  },
  {
    labelColor: '#00BFFF',
    border: 'rgba(0,191,255,0.50)',
    bg: 'rgba(0,80,120,0.20)',
  },
];

const brl = (v: number) =>
  v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const formatContract = (v: number) =>
  v >= 1_000_000
    ? `R$ ${(v / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: v % 1_000_000 === 0 ? 0 : 1 })} mi`
    : `R$ ${(v / 1_000).toLocaleString('pt-BR')} mil`;

const sliderPct = (v: number) => (((v - MIN) / (MAX - MIN)) * 100).toFixed(2);

const formatRange = (tier: IMedal) => {
  const { starts, ends } = tier.range;
  if (ends >= 999_999_999) return `${starts}+ pts`;
  return `${starts}–${ends} pts`;
};

const S = {
  wrap: {
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
    background: 'linear-gradient(135deg, #0d1628 0%, #0B1120 100%)',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.12)',
    padding: '28px 28px 20px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
    color: '#fff',
  } as React.CSSProperties,
  header: { marginBottom: 20 } as React.CSSProperties,
  title: {
    fontSize: 17,
    fontWeight: 800,
    color: '#fff',
    marginBottom: 2,
  } as React.CSSProperties,
  subtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.45)',
  } as React.CSSProperties,

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  } as React.CSSProperties,
  labelSm: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.55)',
  } as React.CSSProperties,
  valueLabel: {
    fontSize: 18,
    fontWeight: 900,
    color: '#fff',
  } as React.CSSProperties,
  rangeLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    marginTop: 4,
    marginBottom: 20,
  } as React.CSSProperties,

  topBox: {
    borderRadius: 14,
    background: 'linear-gradient(135deg, #003a52, #001e38)',
    border: '1px solid rgba(0,191,255,0.50)',
    padding: '18px 16px',
    textAlign: 'center',
    marginBottom: 14,
    boxShadow: '0 0 28px rgba(0,191,255,0.12)',
  } as React.CSSProperties,
  topBoxLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(0,191,255,0.8)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 6,
  } as React.CSSProperties,
  topBoxValue: {
    fontSize: 52,
    fontWeight: 900,
    color: '#fff',
    lineHeight: 1,
    marginBottom: 4,
  } as React.CSSProperties,
  topBoxSub: {
    fontSize: 13,
    fontWeight: 700,
    color: '#00BFFF',
  } as React.CSSProperties,

  levelCard: (border: string, bg: string): React.CSSProperties => ({
    borderRadius: 12,
    border: `1px solid ${border}`,
    background: bg,
    padding: '10px 6px',
    textAlign: 'center',
  }),
  levelIcon: {
    width: 20,
    height: 20,
    objectFit: 'contain',
    marginBottom: 4,
    display: 'block',
    margin: '0 auto 4px',
  } as React.CSSProperties,
  levelName: (color: string): React.CSSProperties => ({
    fontSize: 11,
    fontWeight: 700,
    color,
    marginBottom: 4,
  }),
  levelValue: {
    fontSize: 14,
    fontWeight: 900,
    color: '#fff',
    lineHeight: 1.2,
  } as React.CSSProperties,
  levelReq: {
    fontSize: 9,
    color: 'rgba(255,255,255,0.40)',
    marginTop: 4,
  } as React.CSSProperties,

  compareBox: {
    borderRadius: 14,
    border: '1px solid rgba(0,191,255,0.35)',
    background:
      'linear-gradient(to right, rgba(0,191,255,0.08), rgba(0,80,120,0.15))',
    padding: '14px 16px',
    marginBottom: 12,
  } as React.CSSProperties,
  compareTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: 'rgba(0,191,255,0.65)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    textAlign: 'center',
    marginBottom: 10,
  } as React.CSSProperties,
  compareGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  } as React.CSSProperties,
  compareCard: (highlight: boolean): React.CSSProperties => ({
    borderRadius: 10,
    background: highlight ? 'rgba(0,191,255,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${highlight ? 'rgba(0,191,255,0.45)' : 'rgba(255,255,255,0.08)'}`,
    padding: '10px 8px',
    textAlign: 'center',
    boxShadow: highlight ? '0 0 14px rgba(0,191,255,0.12)' : 'none',
  }),
  compareCardLabel: (highlight: boolean): React.CSSProperties => ({
    fontSize: 9,
    fontWeight: 700,
    color: highlight ? '#00BFFF' : 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  }),
  compareCardValue: (highlight: boolean): React.CSSProperties => ({
    fontSize: 19,
    fontWeight: 900,
    color: highlight ? '#00BFFF' : 'rgba(255,255,255,0.35)',
    textDecoration: highlight ? 'none' : 'line-through',
  }),

  footnote: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: 1.5,
  } as React.CSSProperties,
};

interface Props {
  tiers: IMedal[];
  commissionRate: number /* percentage, e.g. 2 = 2% */;
}

export function CalculadoraComissao({ tiers, commissionRate }: Props) {
  injectStyles();
  const [value, setValue] = useState(400000);
  const pct = sliderPct(value);

  const baseRate = commissionRate / 100;
  const topTier = tiers[tiers.length - 1] ?? null;
  const firstTier = tiers[0] ?? null;
  const cols = Math.min(tiers.length, 4);

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <p style={S.title}>💰 Calcule sua comissão</p>
        <p style={S.subtitle}>
          Arraste para simular diferentes valores de contrato
        </p>
      </div>

      <div style={S.row}>
        <span style={S.labelSm}>Valor do contrato</span>
        <span style={S.valueLabel}>{formatContract(value)}</span>
      </div>
      <input
        type="range"
        min={MIN}
        max={MAX}
        step={STEP}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="gmc-slider"
        style={{
          background: `linear-gradient(to right, #00BFFF ${pct}%, rgba(255,255,255,0.15) ${pct}%)`,
        }}
      />
      <div style={S.rangeLabels}>
        <span>R$ 50k</span>
        <span>R$ 2mi</span>
      </div>

      {/* Top highlight — best tier */}
      <div style={S.topBox}>
        <p style={S.topBoxLabel}>
          Potencial real de ganho · nível {topTier?.name ?? 'máximo'}
          {topTier?.imageUrl && (
            <img
              src={topTier.imageUrl}
              alt={topTier.name}
              style={{
                width: 16,
                height: 16,
                objectFit: 'contain',
                verticalAlign: 'middle',
                marginLeft: 6,
              }}
            />
          )}
        </p>
        <p style={S.topBoxValue}>
          {brl(value * baseRate * (topTier?.bonus ?? 1))}
        </p>
        <p style={S.topBoxSub}>por contrato indicado</p>
      </div>

      {/* Tier cards grid */}
      {tiers.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 8,
            marginBottom: 14,
          }}
        >
          {tiers.map((tier, i) => {
            const palette = TIER_PALETTE[i % TIER_PALETTE.length];
            return (
              <div
                key={tier.name}
                style={S.levelCard(palette.border, palette.bg)}
              >
                {tier.imageUrl ? (
                  <img
                    src={tier.imageUrl}
                    alt={tier.name}
                    style={S.levelIcon}
                  />
                ) : null}
                <p style={S.levelName(palette.labelColor)}>{tier.name}</p>
                <p style={S.levelValue}>{brl(value * baseRate * tier.bonus)}</p>
                <p style={S.levelReq}>{formatRange(tier)}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Compare section */}
      <div style={S.compareBox}>
        <p style={S.compareTitle}>📊 Compare e decida</p>
        <div style={S.compareGrid}>
          <div style={S.compareCard(false)}>
            <p style={S.compareCardLabel(false)}>Correspondente tradicional</p>
            <p style={S.compareCardValue(false)}>{brl(value * 0.015)}</p>
          </div>
          <div style={S.compareCard(true)}>
            <p style={S.compareCardLabel(true)}>
              🚀 Aqui no {firstTier?.name ?? 'programa'}
            </p>
            <p style={S.compareCardValue(true)}>
              {brl(value * baseRate * (firstTier?.bonus ?? 1))}
            </p>
          </div>
        </div>
      </div>

      <p style={S.footnote}>
        💡 A comissão é calculada sobre o valor total do crédito liberado. Sem
        teto de comissão. Valores sujeitos à política comercial vigente.
      </p>
    </div>
  );
}
